<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import KanbanAPI from 'dashboard/api/kanban';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  // embutido num painel: sem overlay, sem moldura de modal e com padding menor
  embedded: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);

const store = useStore();
const { t } = useI18n();

const pipelines = computed(() => store.getters['kanban/pipelines'] ?? []);
const accountId = computed(() => store.getters.getCurrentAccount?.id);

const step = ref(1);
const csvFile = ref(null);
const csvHeaders = ref([]);
const csvPreviewRows = ref([]);
const csvTotalRows = ref(0);
const fieldMapping = ref({});
const selectedPipelineIds = ref([]);
const columnByPipeline = ref({});
const importing = ref(false);
const importError = ref('');
const mappingError = ref('');
const pipelineError = ref('');
const successMessage = ref('');
const opportunityAttributes = ref([]);
const isDragging = ref(false);
const fileInput = ref(null);
const openDropdown = ref(null);
const fieldDropdownSearch = ref({});
const columnDropdownSearch = ref({});
// fecha o modal após o aviso de sucesso; precisa ser cancelável para não rodar
// sobre um componente já destruído (a aba embutida troca sem desmontar o modal)
let closeTimer = null;

const CONTACT_FIELDS = [
  { key: 'contact_name', label: 'Name' },
  { key: 'contact_email', label: 'Email' },
  { key: 'contact_phone', label: 'Phone' },
  { key: 'contact_company', label: 'Company' },
  { key: 'contact_city', label: 'City' },
];

const CARD_FIELDS = [
  { key: 'title', label: 'Title' },
  { key: 'value', label: 'Value' },
  { key: 'closing_date', label: 'Closing date' },
  { key: 'closing_time', label: 'Closing time' },
  { key: 'status', label: 'Status' },
  { key: 'loss_reason', label: 'Loss reason' },
  { key: 'assignee_email', label: 'Assignee email' },
];

const AUTO_MAP = {
  nome: 'contact_name',
  name: 'contact_name',
  'contact name': 'contact_name',
  email: 'contact_email',
  'e-mail': 'contact_email',
  'contact email': 'contact_email',
  telefone: 'contact_phone',
  phone: 'contact_phone',
  celular: 'contact_phone',
  whatsapp: 'contact_phone',
  empresa: 'contact_company',
  company: 'contact_company',
  cidade: 'contact_city',
  city: 'contact_city',
  titulo: 'title',
  title: 'title',
  valor: 'value',
  value: 'value',
  'data prevista': 'closing_date',
  'closing date': 'closing_date',
  previsao: 'closing_date',
  'closing time': 'closing_time',
  status: 'status',
  'loss reason': 'loss_reason',
  'motivo de perda': 'loss_reason',
  'assignee email': 'assignee_email',
};

function normalize(str) {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

async function loadOpportunityAttributes() {
  try {
    const { data } = await KanbanAPI.getOpportunityAttributes();
    opportunityAttributes.value = Array.isArray(data)
      ? data
      : (data.data ?? []);
  } catch {
    opportunityAttributes.value = [];
  }
}

function autoMap(headers) {
  return headers.reduce((acc, header) => {
    const norm = normalize(header);
    if (AUTO_MAP[norm]) {
      return { ...acc, [header]: AUTO_MAP[norm] };
    }
    const customAttr = opportunityAttributes.value.find(
      a => normalize(a.attribute_display_name) === norm
    );
    if (customAttr) {
      return {
        ...acc,
        [header]: `custom_attributes.${customAttr.attribute_key}`,
      };
    }
    return { ...acc, [header]: '__ignore__' };
  }, {});
}

// Parseia o texto inteiro de uma vez (não linha a linha) para não quebrar
// campos entre aspas que contenham quebras de linha embutidas — um padrão
// válido em CSV (RFC 4180). Usado só para a preview local; a importação
// real é feita pela gem CSV do Ruby no backend.
function parseCSVRows(text) {
  const rows = [];
  let row = [];
  let current = '';
  let inQuotes = false;
  const chars = Array.from(text);

  for (let i = 0; i < chars.length; i += 1) {
    const ch = chars[i];
    if (ch === '"') {
      if (inQuotes && chars[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      row.push(current.trim());
      current = '';
    } else if ((ch === '\n' || ch === '\r') && !inQuotes) {
      if (ch === '\r' && chars[i + 1] === '\n') i += 1;
      row.push(current.trim());
      current = '';
      // a gem CSV do Ruby só descarta a linha totalmente em branco; uma linha como
      // ",,," vira um registro de campos vazios. Espelhar isso mantém a contagem da
      // preview igual à quantidade de cards que o backend vai criar.
      if (row.length > 1 || row[0] !== '') rows.push(row);
      row = [];
    } else {
      current += ch;
    }
  }
  // sem conteúdo pendente, o arquivo terminou em quebra de linha e não há
  // última linha de verdade para registrar
  if (current !== '' || row.length) {
    row.push(current.trim());
    if (row.length > 1 || row[0] !== '') rows.push(row);
  }

  return rows;
}

function parseCSV(text) {
  const lines = parseCSVRows(text);
  if (!lines.length) return { headers: [], rows: [], total: 0 };
  const [headers, ...dataLines] = lines;
  const rows = dataLines.slice(0, 3);
  return { headers, rows, total: dataLines.length };
}

function handleFileSelect(file) {
  if (!file || !file.name.endsWith('.csv')) return;
  csvFile.value = file;
  const reader = new FileReader();
  reader.onload = e => {
    const { headers, rows, total } = parseCSV(e.target.result);
    csvHeaders.value = headers;
    csvPreviewRows.value = rows;
    csvTotalRows.value = total;
  };
  reader.readAsText(file);
}

function onFileInput(e) {
  handleFileSelect(e.target.files[0]);
}

function onDrop(e) {
  isDragging.value = false;
  handleFileSelect(e.dataTransfer.files[0]);
}

function removeFile() {
  csvFile.value = null;
  csvHeaders.value = [];
  csvPreviewRows.value = [];
  csvTotalRows.value = 0;
  fieldMapping.value = {};
  // sem isso, reescolher o mesmo arquivo não dispara o change
  if (fileInput.value) fileInput.value.value = '';
}

function goToStep2() {
  if (!csvFile.value) return;
  fieldMapping.value = autoMap(csvHeaders.value);
  step.value = 2;
  mappingError.value = '';
}

function validateMapping() {
  const contactIdentifiers = ['contact_name', 'contact_email', 'contact_phone'];
  return Object.values(fieldMapping.value).some(v =>
    contactIdentifiers.includes(v)
  );
}

function goToStep3() {
  mappingError.value = '';
  if (!validateMapping()) {
    mappingError.value = t('KANBAN.IMPORT.STEP_2_ERROR');
    return;
  }
  step.value = 3;
  pipelineError.value = '';
}

function goBack() {
  if (step.value > 1) step.value -= 1;
}

function togglePipeline(id) {
  const idx = selectedPipelineIds.value.indexOf(id);
  if (idx === -1) {
    selectedPipelineIds.value = [...selectedPipelineIds.value, id];
  } else {
    selectedPipelineIds.value = selectedPipelineIds.value.filter(p => p !== id);
    const next = { ...columnByPipeline.value };
    delete next[id];
    columnByPipeline.value = next;
  }
}

const rowCount = computed(() => csvTotalRows.value);

const validPipelineCount = computed(
  () =>
    selectedPipelineIds.value.filter(id => columnByPipeline.value[id]).length
);

function columnsForPipeline(pipelineId) {
  return pipelines.value.find(p => p.id === pipelineId)?.columns ?? [];
}

function fieldLabel(fieldKey) {
  if (!fieldKey || fieldKey === '__ignore__')
    return t('KANBAN.IMPORT.STEP_2_IGNORE');
  const contact = CONTACT_FIELDS.find(f => f.key === fieldKey);
  if (contact) return contact.label;
  const card = CARD_FIELDS.find(f => f.key === fieldKey);
  if (card) return card.label;
  if (fieldKey.startsWith('custom_attributes.')) {
    const attrKey = fieldKey.replace('custom_attributes.', '');
    const attr = opportunityAttributes.value.find(
      a => a.attribute_key === attrKey
    );
    return attr?.attribute_display_name ?? attrKey;
  }
  return fieldKey;
}

const fieldOptions = computed(() => {
  const opts = [
    {
      value: '__ignore__',
      label: t('KANBAN.IMPORT.STEP_2_IGNORE'),
      group: null,
    },
  ];
  CONTACT_FIELDS.forEach(f =>
    opts.push({
      value: f.key,
      label: f.label,
      group: t('KANBAN.IMPORT.STEP_2_GROUP_CONTACT'),
    })
  );
  CARD_FIELDS.forEach(f =>
    opts.push({
      value: f.key,
      label: f.label,
      group: t('KANBAN.IMPORT.STEP_2_GROUP_CARD'),
    })
  );
  opportunityAttributes.value.forEach(a =>
    opts.push({
      value: `custom_attributes.${a.attribute_key}`,
      label: a.attribute_display_name,
      group: t('KANBAN.IMPORT.STEP_2_GROUP_CUSTOM'),
    })
  );
  return opts;
});

function toggleFieldDropdown(header) {
  openDropdown.value =
    openDropdown.value === `field-${header}` ? null : `field-${header}`;
  if (!fieldDropdownSearch.value[header]) {
    fieldDropdownSearch.value = { ...fieldDropdownSearch.value, [header]: '' };
  }
}

function filteredFieldOptions(header) {
  const q = (fieldDropdownSearch.value[header] ?? '').toLowerCase();
  if (!q) return fieldOptions.value;
  return fieldOptions.value.filter(o => o.label.toLowerCase().includes(q));
}

function groupedFilteredFieldOptions(header) {
  return filteredFieldOptions(header).reduce((groups, opt) => {
    const g = opt.group ?? '__top__';
    return { ...groups, [g]: [...(groups[g] ?? []), opt] };
  }, {});
}

function selectFieldMapping(header, value) {
  fieldMapping.value = { ...fieldMapping.value, [header]: value };
  openDropdown.value = null;
  fieldDropdownSearch.value = { ...fieldDropdownSearch.value, [header]: '' };
}

function toggleColumnDropdown(pipelineId) {
  openDropdown.value =
    openDropdown.value === `col-${pipelineId}` ? null : `col-${pipelineId}`;
  if (!columnDropdownSearch.value[pipelineId]) {
    columnDropdownSearch.value = {
      ...columnDropdownSearch.value,
      [pipelineId]: '',
    };
  }
}

function filteredColumns(pipelineId) {
  const q = (columnDropdownSearch.value[pipelineId] ?? '').toLowerCase();
  const cols = columnsForPipeline(pipelineId);
  if (!q) return cols;
  return cols.filter(c => c.name.toLowerCase().includes(q));
}

function selectColumn(pipelineId, columnId) {
  columnByPipeline.value = {
    ...columnByPipeline.value,
    [pipelineId]: columnId,
  };
  openDropdown.value = null;
  columnDropdownSearch.value = {
    ...columnDropdownSearch.value,
    [pipelineId]: '',
  };
}

function columnNameById(pipelineId, columnId) {
  return (
    columnsForPipeline(pipelineId).find(c => c.id === columnId)?.name ?? ''
  );
}

function onClickOutside(e) {
  if (!e.target.closest('[data-import-modal]')) {
    openDropdown.value = null;
  }
}

onMounted(() => {
  loadOpportunityAttributes();
  document.addEventListener('mousedown', onClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('mousedown', onClickOutside);
  clearTimeout(closeTimer);
});

function resetState() {
  clearTimeout(closeTimer);
  step.value = 1;
  csvFile.value = null;
  csvHeaders.value = [];
  csvPreviewRows.value = [];
  fieldMapping.value = {};
  selectedPipelineIds.value = [];
  columnByPipeline.value = {};
  importError.value = '';
  mappingError.value = '';
  pipelineError.value = '';
  successMessage.value = '';
  openDropdown.value = null;
  if (fileInput.value) fileInput.value.value = '';
}

watch(
  () => props.modelValue,
  val => {
    if (!val) resetState();
  }
);

function close() {
  // embutido não tem o que fechar: volta ao início para uma nova importação
  if (props.embedded) {
    resetState();
    return;
  }
  emit('update:modelValue', false);
}

async function doImport() {
  pipelineError.value = '';
  importError.value = '';

  const validTargets = selectedPipelineIds.value
    .filter(id => columnByPipeline.value[id])
    .map(id => ({ pipeline_id: id, column_id: columnByPipeline.value[id] }));

  if (!validTargets.length) {
    pipelineError.value = t('KANBAN.IMPORT.STEP_3_ERROR');
    return;
  }

  const cleanMapping = Object.entries(fieldMapping.value).reduce(
    (acc, [header, fieldKey]) => {
      if (fieldKey && fieldKey !== '__ignore__') {
        return { ...acc, [header]: fieldKey };
      }
      return acc;
    },
    {}
  );

  importing.value = true;
  try {
    const formData = new FormData();
    formData.append('import_file', csvFile.value);
    formData.append('mapping', JSON.stringify(cleanMapping));
    formData.append('pipeline_targets', JSON.stringify(validTargets));

    await axios.post(
      `/api/v1/accounts/${accountId.value}/kanban_card_imports`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    successMessage.value = t('KANBAN.IMPORT.SUCCESS');
    closeTimer = setTimeout(() => close(), 2500);
  } catch {
    importError.value = t('KANBAN.IMPORT.ERROR');
  } finally {
    importing.value = false;
  }
}
</script>

<template>
  <div
    v-if="modelValue"
    :class="
      embedded
        ? 'flex flex-col h-full min-h-0'
        : 'fixed inset-0 z-50 flex items-center justify-center bg-black/40'
    "
    @click.self="!embedded && close()"
  >
    <div
      data-import-modal
      :class="
        embedded
          ? 'flex flex-col h-full min-h-0 w-full'
          : 'bg-n-background rounded-xl shadow-xl w-full md:w-[640px] mx-4 md:mx-0 max-h-[85vh] flex flex-col relative'
      "
    >
      <button
        v-if="!embedded"
        class="absolute top-4 right-4 text-n-slate-9 hover:text-n-slate-12 z-10"
        @click="close"
      >
        <span class="i-lucide-x size-5" />
      </button>

      <div :class="embedded ? 'flex-shrink-0' : 'p-6 pb-4 flex-shrink-0'">
        <h2 v-if="!embedded" class="text-lg font-semibold text-n-slate-12 mb-5">
          {{ t('KANBAN.IMPORT.TITLE') }}
        </h2>

        <div class="flex items-center gap-2 mb-6">
          <template v-for="s in 3" :key="s">
            <div
              class="size-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors"
              :class="
                step === s
                  ? 'bg-n-brand text-white'
                  : step > s
                    ? 'bg-n-teal-9 text-white'
                    : 'bg-n-surface-2 text-n-slate-10 border border-n-weak'
              "
            >
              <span v-if="step > s" class="i-lucide-check size-3.5" />
              <span v-else>{{ s }}</span>
            </div>
            <div
              v-if="s < 3"
              class="flex-1 h-px transition-colors"
              :class="step > s ? 'bg-n-teal-9' : 'bg-n-weak'"
            />
          </template>
        </div>
      </div>

      <div
        :class="
          embedded
            ? 'overflow-y-auto flex-1 min-h-0'
            : 'px-6 overflow-y-auto flex-1 min-h-0'
        "
      >
        <!-- Step 1: Upload -->
        <template v-if="step === 1">
          <p class="text-sm text-n-slate-11 mb-4">
            {{ t('KANBAN.IMPORT.STEP_1_DESC') }}
          </p>

          <!-- fora do v-if do dropzone para a ref sobreviver à troca de estado -->
          <input
            ref="fileInput"
            type="file"
            accept=".csv"
            class="hidden"
            @change="onFileInput"
          />

          <div
            v-if="!csvFile"
            class="border-2 border-dashed rounded-xl p-10 text-center transition-colors"
            :class="
              isDragging
                ? 'border-n-brand bg-n-alpha-2'
                : 'border-n-weak hover:border-n-container'
            "
            @dragover.prevent="isDragging = true"
            @dragleave="isDragging = false"
            @drop.prevent="onDrop"
          >
            <span
              class="i-lucide-upload-cloud size-10 text-n-slate-10 mx-auto mb-3 block"
            />
            <p class="text-sm text-n-slate-11 mb-2">
              {{ t('KANBAN.IMPORT.DRAG_OR') }}
            </p>
            <button
              type="button"
              class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-n-brand text-white rounded-lg cursor-pointer hover:brightness-110 transition-all"
              @click="fileInput?.click()"
            >
              <span class="i-lucide-folder-open size-4" />
              {{ t('KANBAN.IMPORT.CHOOSE_FILE') }}
            </button>
          </div>

          <div
            v-else
            class="border border-n-weak rounded-xl p-4 bg-n-surface-2"
          >
            <div class="flex items-center gap-3 mb-3">
              <span
                class="i-lucide-file-text size-5 text-n-brand flex-shrink-0"
              />
              <span class="text-sm font-medium text-n-slate-12 truncate flex-1">
                {{ csvFile.name }}
              </span>
              <button
                class="text-xs text-n-ruby-11 hover:text-n-ruby-9 flex items-center gap-1"
                @click="removeFile"
              >
                <span class="i-lucide-trash-2 size-3.5" />
                {{ t('KANBAN.IMPORT.REMOVE_FILE') }}
              </button>
            </div>

            <div
              v-if="csvHeaders.length"
              class="overflow-x-auto rounded-lg border border-n-weak"
            >
              <table class="w-full text-xs">
                <thead>
                  <tr class="bg-n-surface-2">
                    <th
                      v-for="header in csvHeaders"
                      :key="header"
                      class="px-3 py-2 text-left font-medium text-n-slate-10 border-b border-n-weak whitespace-nowrap"
                    >
                      {{ header }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, ri) in csvPreviewRows"
                    :key="ri"
                    class="border-b border-n-weak last:border-0"
                  >
                    <td
                      v-for="(cell, ci) in row"
                      :key="ci"
                      class="px-3 py-2 text-n-slate-11 whitespace-nowrap max-w-[160px] truncate"
                    >
                      {{ cell }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>

        <!-- Step 2: Mapping -->
        <template v-if="step === 2">
          <div class="overflow-x-auto rounded-xl border border-n-weak">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-n-surface-2">
                  <th
                    class="px-4 py-2.5 text-left font-medium text-n-slate-10 border-b border-n-weak w-1/3"
                  >
                    {{ t('KANBAN.IMPORT.STEP_2_COL_CSV') }}
                  </th>
                  <th
                    class="px-4 py-2.5 text-left font-medium text-n-slate-10 border-b border-n-weak w-1/3"
                  >
                    {{ t('KANBAN.IMPORT.STEP_2_COL_EXAMPLES') }}
                  </th>
                  <th
                    class="px-4 py-2.5 text-left font-medium text-n-slate-10 border-b border-n-weak w-1/3"
                  >
                    {{ t('KANBAN.IMPORT.STEP_2_COL_FIELD') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="header in csvHeaders"
                  :key="header"
                  class="border-b border-n-weak last:border-0"
                >
                  <td class="px-4 py-2.5 text-n-slate-12 font-medium">
                    {{ header }}
                  </td>
                  <td class="px-4 py-2.5 text-n-slate-10">
                    <template
                      v-for="(row, ri) in csvPreviewRows.slice(0, 2)"
                      :key="ri"
                    >
                      <span class="truncate inline-block max-w-[120px]">{{
                        row[csvHeaders.indexOf(header)]
                      }}</span>
                      <span
                        v-if="ri < Math.min(csvPreviewRows.length, 2) - 1"
                        class="text-n-slate-9 mx-0.5"
                        >,</span
                      >
                    </template>
                  </td>
                  <td class="px-4 py-2.5">
                    <div class="relative" @click.stop>
                      <button
                        class="flex items-center gap-1 w-full border rounded-lg px-2.5 py-1.5 text-sm transition-colors focus:outline-none text-left"
                        :class="
                          fieldMapping[header] &&
                          fieldMapping[header] !== '__ignore__'
                            ? 'border-n-brand text-n-brand bg-n-alpha-2'
                            : 'border-n-weak text-n-slate-11 bg-n-background hover:border-n-container'
                        "
                        @click="toggleFieldDropdown(header)"
                      >
                        <span class="flex-1 truncate">
                          {{ fieldLabel(fieldMapping[header]) }}
                        </span>
                        <span
                          class="i-lucide-chevron-down size-3 text-n-slate-10 flex-shrink-0"
                        />
                      </button>

                      <div
                        v-if="openDropdown === `field-${header}`"
                        class="absolute left-0 top-9 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-56 overflow-hidden py-1 max-h-60 overflow-y-auto"
                        @click.stop
                      >
                        <template
                          v-for="(opts, group) in groupedFilteredFieldOptions(
                            header
                          )"
                          :key="group"
                        >
                          <p
                            v-if="group !== '__top__'"
                            class="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-n-slate-9"
                          >
                            {{ group }}
                          </p>
                          <button
                            v-for="opt in opts"
                            :key="opt.value"
                            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
                            :class="
                              fieldMapping[header] === opt.value
                                ? 'text-n-brand font-medium'
                                : 'text-n-slate-12'
                            "
                            @click="selectFieldMapping(header, opt.value)"
                          >
                            <span class="flex-1 text-left truncate">{{
                              opt.label
                            }}</span>
                            <span
                              v-if="fieldMapping[header] === opt.value"
                              class="i-lucide-check size-3 text-n-brand flex-shrink-0"
                            />
                          </button>
                        </template>
                        <p
                          v-if="!filteredFieldOptions(header).length"
                          class="px-3 py-2 text-sm text-n-slate-10 italic"
                        >
                          {{ t('KANBAN.SETTINGS.NO_RESULTS') }}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p v-if="mappingError" class="text-xs text-n-ruby-11 mt-3">
            {{ mappingError }}
          </p>
        </template>

        <!-- Step 3: Pipelines -->
        <template v-if="step === 3">
          <div class="space-y-2 mb-4">
            <div
              v-for="pipeline in pipelines"
              :key="pipeline.id"
              class="rounded-xl border transition-colors"
              :class="
                selectedPipelineIds.includes(pipeline.id)
                  ? 'border-n-brand bg-n-alpha-2'
                  : 'border-n-weak bg-n-background'
              "
            >
              <div
                class="flex items-center gap-3 px-4 py-3 cursor-pointer"
                @click="togglePipeline(pipeline.id)"
              >
                <div
                  class="size-5 rounded flex items-center justify-center border transition-colors flex-shrink-0"
                  :class="
                    selectedPipelineIds.includes(pipeline.id)
                      ? 'bg-n-brand border-n-brand'
                      : 'border-n-container bg-n-background'
                  "
                >
                  <span
                    v-if="selectedPipelineIds.includes(pipeline.id)"
                    class="i-lucide-check size-3 text-white"
                  />
                </div>
                <span class="text-sm font-medium text-n-slate-12 flex-1">
                  {{ pipeline.name }}
                </span>
              </div>

              <div
                v-if="selectedPipelineIds.includes(pipeline.id)"
                class="px-4 pb-3 pt-0"
              >
                <div class="relative" @click.stop>
                  <button
                    class="flex items-center gap-1 w-full border rounded-lg px-2.5 py-1.5 text-sm transition-colors focus:outline-none"
                    :class="
                      columnByPipeline[pipeline.id]
                        ? 'border-n-brand text-n-brand bg-n-alpha-2'
                        : 'border-n-weak text-n-slate-11 bg-n-background hover:border-n-container'
                    "
                    @click="toggleColumnDropdown(pipeline.id)"
                  >
                    <span class="flex-1 text-left truncate">
                      {{
                        columnByPipeline[pipeline.id]
                          ? columnNameById(
                              pipeline.id,
                              columnByPipeline[pipeline.id]
                            )
                          : t('KANBAN.IMPORT.STEP_3_SELECT_COLUMN')
                      }}
                    </span>
                    <span
                      class="i-lucide-chevron-down size-3 text-n-slate-10 flex-shrink-0"
                    />
                  </button>

                  <div
                    v-if="openDropdown === `col-${pipeline.id}`"
                    class="absolute left-0 top-9 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-full overflow-hidden py-1 max-h-48 overflow-y-auto"
                    @click.stop
                  >
                    <button
                      v-for="col in filteredColumns(pipeline.id)"
                      :key="col.id"
                      class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
                      :class="
                        columnByPipeline[pipeline.id] === col.id
                          ? 'text-n-brand font-medium'
                          : 'text-n-slate-12'
                      "
                      @click="selectColumn(pipeline.id, col.id)"
                    >
                      <span class="flex-1 text-left">{{ col.name }}</span>
                      <span
                        v-if="columnByPipeline[pipeline.id] === col.id"
                        class="i-lucide-check size-3 text-n-brand flex-shrink-0"
                      />
                    </button>
                    <p
                      v-if="!filteredColumns(pipeline.id).length"
                      class="px-3 py-2 text-sm text-n-slate-10 italic"
                    >
                      {{ t('KANBAN.SETTINGS.NO_RESULTS') }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p
            v-if="validPipelineCount > 0 && rowCount > 0"
            class="text-sm text-n-slate-11 text-center py-2"
          >
            {{
              t('KANBAN.IMPORT.STEP_3_PREVIEW', {
                rows: rowCount,
                pipelines: validPipelineCount,
                cards: rowCount * validPipelineCount,
              })
            }}
          </p>

          <p v-if="pipelineError" class="text-xs text-n-ruby-11 mt-1">
            {{ pipelineError }}
          </p>

          <div
            v-if="successMessage"
            class="mt-3 flex items-center gap-2 px-4 py-3 rounded-xl bg-n-teal-2 border border-n-teal-6 text-n-teal-11 text-sm"
          >
            <span class="i-lucide-check-circle size-4 flex-shrink-0" />
            {{ successMessage }}
          </div>

          <p v-if="importError" class="text-xs text-n-ruby-11 mt-1">
            {{ importError }}
          </p>
        </template>
      </div>

      <div
        :class="
          embedded
            ? 'pt-3 flex items-center justify-end gap-2 border-t border-n-weak flex-shrink-0 mt-4'
            : 'p-6 pt-4 flex items-center justify-end gap-2 border-t border-n-weak flex-shrink-0 mt-4'
        "
      >
        <button
          v-if="step > 1"
          class="px-4 py-2 text-sm text-n-slate-11 hover:text-n-slate-12 border border-n-weak rounded-lg transition-colors"
          :disabled="importing"
          @click="goBack"
        >
          {{ t('KANBAN.IMPORT.BACK') }}
        </button>
        <button
          v-if="step < 3"
          class="px-4 py-2 text-sm bg-n-brand text-white rounded-lg hover:brightness-110 transition-all disabled:opacity-50"
          :disabled="step === 1 && !csvFile"
          @click="step === 1 ? goToStep2() : goToStep3()"
        >
          {{ t('KANBAN.IMPORT.NEXT') }}
        </button>
        <button
          v-if="step === 3"
          class="px-4 py-2 text-sm bg-n-brand text-white rounded-lg hover:brightness-110 transition-all disabled:opacity-50"
          :disabled="importing || !!successMessage"
          @click="doImport"
        >
          {{
            importing ? t('KANBAN.IMPORT.IMPORTING') : t('KANBAN.IMPORT.IMPORT')
          }}
        </button>
      </div>
    </div>
  </div>
</template>
